import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { getPortfolio } from '@/api/portfolio';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getPortfolioPageMeta } from '@/seo/meta';
import { localizedName, localizedTitle } from '@/i18n/localize';
import type { PortfolioDetail } from '@/types';

export default function PortfolioDetail() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [portfolio, setPortfolio] = useState<PortfolioDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!slug) return;

    getPortfolio(slug)
      .then(data => {
        setPortfolio(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  usePageMeta(portfolio ? getPortfolioPageMeta(portfolio) : null);

  if (loading) return <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}><p>{t('portfolioDetail.loading')}</p></div>;
  if (error || !portfolio) return <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}><p>{t('portfolioDetail.notFound')}</p></div>;

  const designSection = portfolio.content?.find(c => c.name.toLowerCase() === 'design');
  const statusSection = portfolio.content?.find(c => {
    const n = c.name.toLowerCase();
    return n === 'status' || n === 'statu';
  });

  const mainContent = portfolio.content?.filter(c => {
    const n = c.name.toLowerCase();
    return n !== 'design' && n !== 'status' && n !== 'statu';
  });

  return (
    <>
      {/* Page Header */}
      <div className="page-header parallaxie" style={{
        backgroundImage: portfolio.image ? `url(${portfolio.image})` : 'url(/images/page-header-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        backgroundPosition: 'center',
      }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="page-header-box">
                <h1>{localizedTitle(portfolio, i18n.language)}</h1>
                <nav>
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/">{t('common.home')}</Link></li>
                    <li className="breadcrumb-item"><Link to="/portfolio">{t('portfolio.breadcrumb')}</Link></li>
                    <li className="breadcrumb-item active">{localizedTitle(portfolio, i18n.language)}</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Content */}
      <div className="page-project-single" style={{ padding: '100px 0' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="project-single-content">
                {/* Gallery */}
                {portfolio.gallery && portfolio.gallery.length > 0 && (
                  <div className="project-gallery" style={{ marginBottom: '40px' }}>
                    <h3 style={{ 
                      fontSize: '24px', 
                      fontWeight: '600',
                      marginBottom: '30px',
                      color: 'var(--primary-color, #192324)'
                    }}                    >{t('portfolioDetail.projectGallery')}</h3>
                    <div className="row">
                      {portfolio.gallery.map((img, i) => (
                        <div key={i} className="col-md-6 col-sm-6" style={{ marginBottom: '20px' }}>
                          <div style={{
                            overflow: 'hidden',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}>
                            <img 
                              src={img} 
                              alt={`${portfolio.title} - ${i + 1}`} 
                              loading="lazy"
                              style={{ 
                                width: '100%', 
                                height: '250px',
                                objectFit: 'cover',
                                transition: 'transform 0.3s ease'
                              }}
                              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Featured Image */}
                {portfolio.image && (
                  <div className="project-featured-image" style={{ marginBottom: '40px' }}>
                    <img 
                      src={portfolio.image} 
                      alt={portfolio.title} 
                      style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                    />
                  </div>
                )}

                {/* Description */}
                {portfolio.secondaryDescription && (
                  <div className="project-description" style={{ 
                    marginBottom: '40px', 
                    padding: '30px',
                    background: 'var(--secondery-color, #F8F8F8)',
                    borderLeft: '4px solid var(--accent-color, #7C877F)'
                  }}>
                    <p style={{ fontSize: '18px', lineHeight: '1.8', margin: 0 }}>{portfolio.secondaryDescription}</p>
                  </div>
                )}

                {/* Content Sections */}
                {mainContent?.map((section, i) => (
                  <div key={i} className="project-content-section" style={{ marginBottom: '40px' }}>
                    <h3 style={{ 
                      fontSize: '24px', 
                      fontWeight: '600',
                      marginBottom: '20px',
                      color: 'var(--primary-color, #192324)'
                    }}>{section.name}</h3>
                    <div 
                      style={{ 
                        fontSize: '16px', 
                        lineHeight: '1.8',
                        color: 'var(--text-color, #283132)'
                      }}
                      dangerouslySetInnerHTML={{ __html: section.content }} 
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <div className="project-sidebar" style={{
                position: 'sticky',
                top: '100px'
              }}>
                <div className="project-info-box" style={{
                  background: 'var(--secondery-color, #F8F8F8)',
                  padding: '40px',
                  borderRadius: '8px',
                  marginBottom: '30px'
                }}>
                  <h3 style={{ 
                    fontSize: '20px', 
                    fontWeight: '600',
                    marginBottom: '25px',
                    paddingBottom: '15px',
                    borderBottom: '2px solid var(--primary-color, #192324)',
                    color: 'var(--primary-color, #192324)'
                  }}                  >{t('portfolioDetail.projectInformation')}</h3>
                  
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    <li style={{ 
                      padding: '15px 0', 
                      borderBottom: '1px solid var(--divider-color, #EAF0EC)',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontWeight: '600', color: 'var(--primary-color, #192324)' }}>{t('portfolioDetail.category')}</span>
                      <span style={{ color: 'var(--text-color, #283132)', textAlign: 'right' }}>{portfolio.categories.map(c => localizedName(c, i18n.language)).join(', ')}</span>
                    </li>
                    
                    {portfolio.address && <li style={{ 
                      padding: '15px 0', 
                      borderBottom: '1px solid var(--divider-color, #EAF0EC)',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontWeight: '600', color: 'var(--primary-color, #192324)' }}>{t('portfolioDetail.address')}</span>
                      <span style={{ color: 'var(--text-color, #283132)', textAlign: 'right' }}>{portfolio.address}</span>
                    </li>}

                    {designSection && <li style={{ 
                      padding: '15px 0', 
                      borderBottom: '1px solid var(--divider-color, #EAF0EC)',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontWeight: '600', color: 'var(--primary-color, #192324)' }}>{t('portfolioDetail.design')}</span>
                      <span style={{ color: 'var(--text-color, #283132)', textAlign: 'right' }} dangerouslySetInnerHTML={{ __html: designSection.content }} />
                    </li>}

                    {statusSection && <li style={{ 
                      padding: '15px 0', 
                      borderBottom: '1px solid var(--divider-color, #EAF0EC)',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontWeight: '600', color: 'var(--primary-color, #192324)' }}>{t('portfolioDetail.status')}</span>
                      <span style={{ color: 'var(--text-color, #283132)', textAlign: 'right' }} dangerouslySetInnerHTML={{ __html: statusSection.content }} />
                    </li>}
                    
                    <li style={{ 
                      padding: '15px 0',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{ fontWeight: '600', color: 'var(--primary-color, #192324)' }}>{t('portfolioDetail.projectType')}</span>
                      <span style={{ color: 'var(--text-color, #283132)', textAlign: 'right' }}>{t('portfolioDetail.residential')}</span>
                    </li>
                  </ul>
                </div>

                {/* CTA */}
                <div style={{
                  background: 'var(--primary-color, #192324)',
                  padding: '40px',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <h4 style={{ 
                    color: '#fff', 
                    fontSize: '20px',
                    marginBottom: '15px'
                  }}>{t('portfolioDetail.interested')}</h4>
                  <p style={{ 
                    color: 'rgba(255,255,255,0.8)', 
                    fontSize: '14px',
                    marginBottom: '25px'
                  }}>{t('portfolioDetail.contactIntro')}</p>
                  <Link 
                    to="/contact" 
                    className="btn-default"
                    style={{
                      display: 'inline-block',
                      background: 'var(--accent-color, #7C877F)',
                      color: '#fff'
                    }}
                  >
                    {t('common.getInTouch')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
